import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import type { ZodTypeAny } from 'zod';
import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import AlertBox from '@/components/AlertBox';
import Loader from '@/components/Loader';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { displayError, normalizeAxiosError } from '@/lib/error';
import { displayFullName } from '@/lib/utils';
import { useGetWorkOrderById } from '@/services/workOrderService';
import { useUpdateWorkOrderAttemptMutation } from '@/services/attemptService';
import { updateAttempt as updateHiAttempt } from '@/services/homeInspectionAttemptService';
import { uploadMedia } from '@/services/uploadMedia';
import { useUserStore } from '@/stores/user';
import { buildAttemptImagePreviews } from '@/pages/homeInspection/hiAttemptHelpers';
import { HiAttemptStep1OwnerInfo } from '@/pages/homeInspection/steps/HiAttemptStep1OwnerInfo';
import { HiAttemptStep2TenantInfo } from '@/pages/homeInspection/steps/HiAttemptStep2TenantInfo';
import { HiAttemptStep3PropertyInfo, type PreviewEntry } from '@/pages/homeInspection/steps/HiAttemptStep3PropertyInfo';
import { HiAttemptStep4Comments } from '@/pages/homeInspection/steps/HiAttemptStep4Comments';
import type { WorkOrderAttemptListResponse } from '@/types/workOrderAttempt.types';
import type { ImageType } from '@/types/workOrderAttempt.types';
import { canCreatePsAttempt } from './canCreatePsAttempt';
import {
  getPsFirstAttemptHiEditHeader,
  getPsFirstAttemptHiHeader,
  getPsFirstAttemptHiSteps,
  isVacantAndAbandonedCase,
  resolvePsCaseTypeId,
} from './psFirstAttemptHiConfig';
import {
  PS_FIRST_ATTEMPT_HI_STEP_SCHEMAS,
  type PsFirstAttemptHiFormValues,
} from './psFirstAttemptHi.schema';
import {
  mapPsFirstAttemptToHiUpdatePayload,
  mapPsFirstAttemptToLinkedHiPayload,
  mapPsFirstAttemptToPsCreatePayload,
  mapPsFirstAttemptToPsUpdatePayload,
} from './mapPsFirstAttemptToPayloads';
import { mapPsFirstAttemptHiViewDefaults } from './mapPsFirstAttemptHiViewDefaults';
import { adaptPsHiAttemptToHiGetResponse } from './adaptPsHiAttemptToHiGetResponse';
import { savePsFirstAttemptWithHi } from './savePsFirstAttemptWithHi';
import { savePsFirstAttemptHiEdit } from './savePsFirstAttemptHiEdit';
import { PsFirstAttemptServiceInfoStep } from './steps/PsFirstAttemptServiceInfoStep';
import { PsHiVacancyChecklistStep } from './steps/PsHiVacancyChecklistStep';

type Props = {
  linkedHIWorkOrderID: string;
  caseTypeId: string | null | undefined;
  workOrderNumber: string;
  /** Create (default) or edit the first linked PS+HI attempt. */
  mode?: 'create' | 'edit';
  /** Required for edit — preloaded attempt GET used for prefill. */
  attemptList?: WorkOrderAttemptListResponse;
};

export const PsFirstAttemptHiStepper = ({
  linkedHIWorkOrderID,
  caseTypeId,
  workOrderNumber,
  mode = 'create',
  attemptList,
}: Props) => {
  const isEdit = mode === 'edit';
  const { id: workOrderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const loggedInUser = useUserStore((state) => state.user);
  const updateAttemptMutation = useUpdateWorkOrderAttemptMutation();
  const { data: workOrder, isPending, isError } = useGetWorkOrderById(workOrderId);

  const psAttempt = attemptList?.workOrderAttempts[0];
  const hiAttempt = attemptList?.homeInspectionAttempts?.[0];

  const resolvedCaseTypeId = useMemo(
    () => resolvePsCaseTypeId(workOrder?.service?.case) ?? caseTypeId ?? null,
    [workOrder?.service?.case, caseTypeId]
  );

  const stepConfigs = useMemo(
    () => getPsFirstAttemptHiSteps({ caseTypeId: resolvedCaseTypeId }),
    [resolvedCaseTypeId]
  );
  const header = isEdit
    ? getPsFirstAttemptHiEditHeader(workOrderNumber)
    : getPsFirstAttemptHiHeader(workOrderNumber);
  const includeVacancy = isVacantAndAbandonedCase(resolvedCaseTypeId);

  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(() => stepConfigs.map((s) => ({ title: s.title, isCompleted: false })));
  const [currentSchema, setCurrentSchema] = useState<ZodTypeAny>(PS_FIRST_ATTEMPT_HI_STEP_SCHEMAS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAlertOpen, setSubmitAlertOpen] = useState(false);
  const [previews, setPreviews] = useState<PreviewEntry[]>([]);
  const [prefillReady, setPrefillReady] = useState(!isEdit);
  /** After HI succeeded but PS failed, next save retries PS only. */
  const [psOnlyRetry, setPsOnlyRetry] = useState(false);

  const form = useForm<PsFirstAttemptHiFormValues>({
    resolver: zodResolver(currentSchema),
    mode: 'all',
    defaultValues: {
      serviceAttempt: {
        images: [],
        serverName: '',
      },
      tenants: [],
      mediaIds: [],
      vacancyChecklist: {},
    },
  });

  const exitAfterCancel = () => {
    if (isEdit && workOrderId && psAttempt?.attemptId) {
      navigate(`?mode=view&attemptId=${psAttempt.attemptId}`, { replace: true });
      return;
    }
    navigate(`/process?workOrderId=${workOrderId}`);
  };

  const exitAfterSuccess = () => {
    if (isEdit && workOrderId && psAttempt?.attemptId) {
      navigate(`?mode=view&attemptId=${psAttempt.attemptId}`, { replace: true });
      return;
    }
    navigate(`/process?workOrderId=${workOrderId}`);
  };

  useEffect(() => {
    setSteps(stepConfigs.map((s) => ({ title: s.title, isCompleted: false })));
    setActiveStep(0);
  }, [stepConfigs]);

  useEffect(() => {
    const schemaIndex = Math.min(activeStep, PS_FIRST_ATTEMPT_HI_STEP_SCHEMAS.length - 1);
    setCurrentSchema(PS_FIRST_ATTEMPT_HI_STEP_SCHEMAS[schemaIndex]);
    setTimeout(async () => {
      await form.trigger();
      form.clearErrors();
    }, 50);
  }, [activeStep]);

  useEffect(() => {
    if (isEdit) return;
    if (!workOrder) return;
    const serverName = workOrder.server
      ? displayFullName(workOrder.server)
      : workOrder.vendor?.vendor
        ? workOrder.vendor.vendor.name
        : '';
    form.setValue('serviceAttempt.serverName', serverName);
    const inspectorName = workOrder.server
      ? displayFullName(workOrder.server)
      : workOrder.vendor?.vendor?.name ?? 'No inspector assigned';
    form.setValue('inspectorName', inspectorName);
  }, [workOrder, form, isEdit]);

  useEffect(() => {
    if (!isEdit || !attemptList || !psAttempt || !hiAttempt) return;
    form.reset(
      mapPsFirstAttemptHiViewDefaults({
        listResponse: attemptList,
        psAttempt,
        hiAttempt,
      })
    );
    setPreviews(buildAttemptImagePreviews(adaptPsHiAttemptToHiGetResponse(hiAttempt)));
    setPrefillReady(true);
  }, [isEdit, attemptList, psAttempt, hiAttempt, form]);

  if (!canCreatePsAttempt(loggedInUser)) {
    return <Navigate to="/not-found" />;
  }

  if (isEdit && (!psAttempt || !hiAttempt)) {
    return (
      <div className="flex items-center justify-center h-64 text-error text-sm">
        Failed to load attempt. Please go back and try again.
      </div>
    );
  }

  const nextStep = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;
    setSteps((prev) => prev.map((s, i) => (i === activeStep ? { ...s, isCompleted: true } : s)));
    setActiveStep((prev) => Math.min(prev + 1, stepConfigs.length - 1));
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const goToStep = async (step: number) => {
    const isValid = await form.trigger();
    if (!isValid) return;
    if (steps[step]?.isCompleted || step <= activeStep) {
      setActiveStep(step);
    }
  };

  const uploadPsImages = async (images: ImageType[] | null | undefined): Promise<string[]> => {
    if (!images?.length) return [];
    const ids: string[] = [];
    for (const img of images) {
      if (img.mediaId) {
        ids.push(img.mediaId);
        continue;
      }
      if (img.image instanceof File) {
        const uploaded = await uploadMedia(img.image, 'WORK_ORDER_ATTEMPT');
        if (!uploaded?.mediaId) throw new Error('Upload failed');
        ids.push(uploaded.mediaId);
      }
    }
    return ids;
  };

  const saveHandler = async () => {
    const isValid = await form.trigger();
    if (!isValid || !workOrderId) return;

    setIsSubmitting(true);
    try {
      const values = form.getValues();
      const psMediaIds = await uploadPsImages(values.serviceAttempt.images as ImageType[] | null | undefined);

      if (isEdit) {
        if (!psAttempt?.attemptId || !hiAttempt?.attemptId) {
          toast({
            variant: 'destructive',
            description: 'Attempt identifiers are missing. Cannot save.',
            duration: 5000,
          });
          return;
        }

        const hiMediaIds =
          values.mediaIds?.length ? values.mediaIds : psMediaIds.length ? psMediaIds : [];
        const hiUpdatePayload = mapPsFirstAttemptToHiUpdatePayload(values, {
          includeVacancyChecklist: includeVacancy,
          hiMediaIds,
        });
        const psUpdatePayload = mapPsFirstAttemptToPsUpdatePayload(values, {
          attemptId: psAttempt.attemptId,
          originalMedia: psAttempt.workOrderAttemptMedia,
          currentMediaIds: psMediaIds,
        });

        const result = await savePsFirstAttemptHiEdit(
          {
            psWorkOrderId: workOrderId,
            linkedHIWorkOrderID,
            hiAttemptId: hiAttempt.attemptId,
            psUpdatePayload,
            hiUpdatePayload,
            psOnly: psOnlyRetry,
          },
          {
            patchPsAttempt: (args) => updateAttemptMutation.mutateAsync(args),
            updateHiAttempt,
          }
        );

        if (!result.ok) {
          if (result.failedAt === 'missingLinkedHi') {
            toast({
              variant: 'destructive',
              description: 'Linked Home Inspection work order is missing. Cannot save Home Inspection attempt.',
              duration: 5000,
            });
          } else if (result.failedAt === 'psAfterHi') {
            setPsOnlyRetry(true);
            toast({
              variant: 'destructive',
              description:
                'Home Inspection was updated, but Process Service save failed. Please retry to finish updating the Process Service attempt.',
              duration: 8000,
            });
            void queryClient.invalidateQueries({ queryKey: ['workOrderAttempts', workOrderId] });
          } else {
            displayError(normalizeAxiosError(result.error), 5000);
          }
          return;
        }

        setPsOnlyRetry(false);
        await queryClient.invalidateQueries({ queryKey: ['workOrderAttempts', workOrderId] });
        await queryClient.invalidateQueries({
          queryKey: ['hi-work-order-detail', linkedHIWorkOrderID],
        });

        toast({
          description: 'Status update saved for Process Service and Home Inspection.',
          duration: 5000,
        });
        exitAfterSuccess();
        return;
      }

      const homeInspectionAttempt = mapPsFirstAttemptToLinkedHiPayload(values, {
        includeVacancyChecklist: includeVacancy,
        hiMediaIds: psMediaIds,
      });
      const psPayload = mapPsFirstAttemptToPsCreatePayload(values, psMediaIds, {
        homeInspectionAttempt,
      });

      const result = await savePsFirstAttemptWithHi(
        {
          psWorkOrderId: workOrderId,
          linkedHIWorkOrderID,
          psCreatePayload: psPayload,
          homeInspectionAttempt,
        },
        {
          patchPsAttempt: (args) => updateAttemptMutation.mutateAsync(args),
        }
      );

      if (!result.ok) {
        if (result.failedAt === 'missingLinkedHi') {
          toast({
            variant: 'destructive',
            description: 'Linked Home Inspection work order is missing. Cannot save Home Inspection attempt.',
            duration: 5000,
          });
        } else {
          displayError(normalizeAxiosError(result.error), 5000);
        }
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ['workOrderAttempts', workOrderId] });
      toast({
        description: 'Status update saved for Process Service and Home Inspection.',
        duration: 5000,
      });
      navigate(`/process?workOrderId=${workOrderId}`);
    } catch (err) {
      displayError(normalizeAxiosError(err), 5000);
    } finally {
      setIsSubmitting(false);
      setSubmitAlertOpen(false);
    }
  };

  const stepId = stepConfigs[activeStep]?.id;
  const isLastStep = activeStep === stepConfigs.length - 1;

  const hiWorkOrderStub = {
    createdAt: workOrder?.createdAt ? String(workOrder.createdAt) : new Date().toISOString(),
    server: workOrder?.server
      ? { firstName: workOrder.server.firstName, lastName: workOrder.server.lastName }
      : null,
  };

  return (
    <Loader isLoading={(isPending && !workOrder) || (isEdit && !prefillReady)}>
      {isError || !workOrder ? (
        <div className="flex items-center justify-center h-64 text-error text-sm">
          Failed to load work order. Please go back and try again.
        </div>
      ) : (
        <div className="w-full scrollbar-hide flex flex-col">
          <div className="w-full">
            <ToolBar
              title={header.title}
              secondaryTitle={<p className="text-sm text-placeholder">{header.subtitle}</p>}
            />
            <div className="mt-3 flex justify-center overflow-x-auto pl-24">
              <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} />
            </div>

            <FormProvider {...form}>
              {stepId === 'serviceInformation' && <PsFirstAttemptServiceInfoStep />}
              {stepId === 'ownerInformation' && (
                <HiAttemptStep1OwnerInfo workOrder={hiWorkOrderStub} hideDateTime />
              )}
              {stepId === 'tenantInformation' && <HiAttemptStep2TenantInfo />}
              {stepId === 'propertyInformation' && (
                <div className="flex flex-col gap-6">
                  <HiAttemptStep3PropertyInfo previews={previews} setPreviews={setPreviews} />
                  <HiAttemptStep4Comments workOrder={hiWorkOrderStub} />
                </div>
              )}
              {stepId === 'vacancyChecklist' && <PsHiVacancyChecklistStep />}
            </FormProvider>

            <div className="flex justify-end mt-4 mb-8 w-full gap-2">
              <AlertBox
                triggerButton={
                  <Button
                    variant="outline"
                    className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
                  >
                    Cancel
                  </Button>
                }
                title="Are you sure you want to cancel?"
                description="All of your current changes will be lost."
                warning
                cancelBtnLabel="No"
                confirmBtnLabel="Yes"
                confirmBtnClick={exitAfterCancel}
              />
              {activeStep > 0 && (
                <Button
                  variant="outline"
                  className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
                  onClick={prevStep}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              )}
              {!isLastStep ? (
                <Button
                  className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white disabled:bg-primary"
                  onClick={() => void nextStep()}
                  disabled={!form.formState.isValid}
                >
                  Next
                </Button>
              ) : (
                <AlertBox
                  open={submitAlertOpen}
                  triggerButton={
                    <Button
                      className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white"
                      onClick={() => setSubmitAlertOpen(true)}
                      disabled={!form.formState.isValid || isSubmitting || updateAttemptMutation.isPending}
                      isLoading={isSubmitting || updateAttemptMutation.isPending}
                    >
                      Save
                    </Button>
                  }
                  title="Do you want to save the record?"
                  description={
                    isEdit
                      ? 'This action will update Process Service and Home Inspection attempts.'
                      : 'This action will create Process Service and Home Inspection attempts.'
                  }
                  cancelBtnLabel="Cancel"
                  confirmBtnLabel="Save"
                  cancelBtnClick={() => setSubmitAlertOpen(false)}
                  isLoading={isSubmitting || updateAttemptMutation.isPending}
                  confirmBtnClick={() => void saveHandler()}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </Loader>
  );
};
